import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Visibility, TrendingUp, Security, Error } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../App';

const PaymentAudit = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    userId: '',
    startDate: null,
    endDate: null,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchPaymentAudit = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.append('endDate', filters.endDate.toISOString());

      const response = await axios.get(`${API_BASE_URL}/api/payment-audit?${params.toString()}`);

      setTransactions(response.data.transactions || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Payment audit fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPaymentAudit();
  }, [fetchPaymentAudit]);

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const getStatusColor = (status) => {
    if (status?.includes('completed')) return 'success';
    if (status?.includes('failed')) return 'error';
    if (status?.includes('pending')) return 'warning';
    return 'default';
  };

  const getEventSourceChip = (source) => {
    const color = source === 'webhook' ? 'primary' : 'secondary';
    return <Chip label={source || 'direct'} color={color} size="small" />;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'Transaction ID',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontFamily="monospace">
            {params.value?.substring(0, 8)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: 'userId',
      headerName: 'User ID',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value?.substring(0, 8)}...
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => {
        const type = params.value?.replace('webhook_', '').replace('credits_purchase', 'credits');
        return <Chip label={type} variant="outlined" size="small" />;
      },
    },
    {
      field: 'amountKobo',
      headerName: 'Amount',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          ₦{((params.value || 0) / 100).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value?.replace(/_/g, ' ')}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'eventSource',
      headerName: 'Source',
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => getEventSourceChip(params.value),
    },
    {
      field: 'processedAt',
      headerName: 'Processed At',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'auditData',
      headerName: 'Security',
      flex: 0.3,
      minWidth: 80,
      renderCell: (params) => {
        const hasSecurityFlag = params.value?.securityFlag;
        return hasSecurityFlag ? (
          <Security color="warning" titleAccess="Security alert flagged" />
        ) : null;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.4,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<Visibility />}
          onClick={() => handleViewDetails(params.row)}
        >
          View
        </Button>
      ),
    },
  ];

  const SummaryCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5" component="div" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Payment Audit
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Revenue"
              value={`₦${summary.totalRevenue?.toLocaleString()}`}
              subtitle="All filtered transactions"
              icon={<TrendingUp sx={{ fontSize: 40 }} />}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Transactions"
              value={summary.totalTransactions}
              subtitle={`${summary.successfulTransactions} successful`}
              icon={<TrendingUp sx={{ fontSize: 40 }} />}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Failed Transactions"
              value={summary.failedTransactions}
              subtitle="Require attention"
              icon={<Error sx={{ fontSize: 40 }} />}
              color="error.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Webhook Processed"
              value={summary.webhookProcessed}
              subtitle={`${summary.directApiProcessed} direct API`}
              icon={<Security sx={{ fontSize: 40 }} />}
              color="info.main"
            />
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="completed_webhook">Completed (Webhook)</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="User ID"
                value={filters.userId}
                onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                placeholder="Enter user ID"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Data Grid */}
        <Paper elevation={2} sx={{
          height: { xs: 400, sm: 500, md: 600 },
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
          }
        }}>
          <DataGrid
            rows={transactions}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          />
        </Paper>

        {/* Transaction Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Transaction Details
          </DialogTitle>
          <DialogContent>
            {selectedTransaction && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace" gutterBottom>
                      {selectedTransaction.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User ID
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace" gutterBottom>
                      {selectedTransaction.userId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Amount
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      ₦{((selectedTransaction.amountKobo || 0) / 100).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedTransaction.status}
                      color={getStatusColor(selectedTransaction.status)}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Customer Email
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTransaction.customerEmail || 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedTransaction.auditData?.securityFlag && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        <Typography variant="subtitle2">Security Alert</Typography>
                        <Typography variant="body2">
                          {selectedTransaction.auditData.reason}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Raw Data (JSON)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace'
                      }}
                    >
                      {JSON.stringify(selectedTransaction, null, 2)}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PaymentAudit;
